import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * @description Componente de orden superior para proteger rutas que requieren autenticación
 * y, opcionalmente, un rol específico.
 * Verifica el estado del `currentUser` del hook `useAuth`.
 * Muestra un estado de carga, redirige al login, redirige al dashboard (rol insuficiente)
 * o renderiza los componentes hijos según corresponda.
 * @param {object} props Las propiedades del componente.
 * @param {React.ReactNode} props.children Los componentes hijos a renderizar si el acceso está permitido.
 * @param {string[]} [props.allowedRoles] Lista de roles que pueden acceder a la ruta.
 *   Si se omite, cualquier usuario autenticado puede acceder.
 * @returns {JSX.Element} El componente hijo, una redirección a `/login`, a `/dashboard` o un mensaje de carga.
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser } = useAuth();

  // Si `currentUser` es `undefined`, el estado de autenticación aún no se ha determinado.
  // Se muestra un mensaje de carga para evitar un parpadeo o redirección prematura.
  if (currentUser === undefined) {
    return <div>Cargando...</div>;
  }

  // Si no hay usuario (`null` o `false`), se redirige a la página de login.
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Si se especificaron roles permitidos y el rol del usuario no está incluido,
  // se redirige al dashboard en lugar de mostrar un error o pantalla en blanco.
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    return <Navigate to="/dashboard" />;
  }

  // El usuario está autenticado y tiene el rol requerido — se renderizan los hijos.
  return children;
};

export default ProtectedRoute;
